// Libraries
import { Grid, Skeleton, Typography } from "@mui/material";
import cloneDeep from "lodash-es/cloneDeep";
import { useEffect, useState } from "react";
import clsx from "clsx";

// Components
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import InputFormExtension from "@/Layouts/components/InputComponents/InputFormExtension";

// Interfaces and Types
import { IModifierDefaultTradeAmount, ITypeDefaultTradeAmount } from "@/Settings/types/administrationTools/model";
import { TOnSaveAmount } from "@/Settings/types/administrationTools/dealerTradeAmountValue";

type TVehicleSectionAmountProps = {
	typeVehicle: "newVehicle" | "preOwnedVehicle";
	title: string;
	referenceOptions: string[];
	value: ITypeDefaultTradeAmount;
	isLoading: boolean;
	onSave: (obj: TOnSaveAmount) => void;
};

export default function VehicleSectionAmount(props: Readonly<TVehicleSectionAmountProps>) {
	const { typeVehicle, title, referenceOptions, value, isLoading, onSave } = props;

	const [reference, setReference] = useState<string>("");
	const [modifier, setModifier] = useState<IModifierDefaultTradeAmount>({} as IModifierDefaultTradeAmount);

	function handleReferenceChange(option: { label: string; value: string }) {
		const obj = { reference: option.value, modifier };
		setReference(obj.reference);
		onSave({ type: typeVehicle, obj });
	}

	function handleModifierChange(modifierObj: { value: number; type: "Dollar" | "Percent" }) {
		const obj = { reference, modifier: modifierObj };
		setModifier(modifierObj);
		onSave({ type: typeVehicle, obj });
	}

	useEffect(() => {
		setReference(value.reference);
		setModifier(cloneDeep(value.modifier));
	}, [value]);

	return (
		<Grid item container direction={"column"} xs={12}>
			<Grid item sm={4} mb={3}>
				<Typography className="!font-montserrat !text-default !text-[20px] !font-medium">{title}</Typography>
			</Grid>
			<Grid item sm={4} container direction={"row"} spacing={3}>
				<Grid item sm={6}>
					<Typography className="!font-montserrat !text-default !text-[16px] !font-medium !mb-4">Reference</Typography>
					{isLoading ? (
						<Skeleton width={170} height={49} className="!mt-[-11px] !mb-[-11px]" />
					) : (
						<SmartSelector
							selectBy={"value"}
							showBy={"value"}
							selected={reference}
							inputProps={{
								placeholder: "Select Reference",
							}}
							sx={{ minWidth: "84px" }}
							onChangeAction={(option) => handleReferenceChange(option as { label: string; value: string })}
							options={referenceOptions.map((option) => ({ label: option, value: option }))}
						/>
					)}
				</Grid>
				<Grid item sm={1} alignContent={"end"} className={clsx({ "!pl-1 text-center": true })}>
					<Typography
						className={clsx({
							"!font-montserrat !text-default !text-[16px] !font-medium content-center": true,
							"h-[30px]": !isLoading,
							"h-[25px]": isLoading,
						})}
					>
						+
					</Typography>
				</Grid>
				<Grid item sm={5} className="!pl-1">
					<Typography className="!font-montserrat !text-default !text-[16px] !font-medium !mb-4">Modifier</Typography>
					{isLoading ? (
						<Skeleton width={95} height={49} className="!mt-[-11px] !mb-[-11px]" />
					) : (
						<InputFormExtension
							data={[
								{
									type: "Percent",
									min: Number.NEGATIVE_INFINITY,
									max: Number.MAX_VALUE,
									decimalsEditable: true,
									decimals: 2,
									delta: 0.01,
								},
								{
									type: "Dollar",
									min: Number.NEGATIVE_INFINITY,
									max: Number.MAX_VALUE,
									decimalsEditable: true,
									decimals: 2,
									delta: 0.01,
								},
							]}
							value={modifier.value ?? ""}
							defaultType={modifier.type}
							onChangeType={(_, input) => {
								handleModifierChange({
									...modifier,
									type: input as "Dollar" | "Percent",
									value: modifier.value ?? 0,
								});
							}}
							onActionUpdate={(n) => {
								handleModifierChange({ ...modifier, value: Number(n) });
							}}
							sx={{
								width: "95px",
							}}
						/>
					)}
				</Grid>
			</Grid>
		</Grid>
	);
}
